import { useState, useEffect } from 'react'; 
import './MemberCard.css';
import { BsThreeDots } from 'react-icons/bs';
import { deleteStudent, updateStudent } from '../../../../api/studentsAPI';
import { getIdFromCommittee, deleteCommitteeMembership, getCommittees, addCommitteeMembership } from '../../../../api/committeesAPI';

const getTagClass = (tag) => {
    if (!tag || typeof tag !== 'string') {
        return 'tag-gray';
    }
    const lowerTag = tag.toLowerCase();

    // House Styles
    if (lowerTag.includes('phoenix')) return 'tag-red';
    if (lowerTag.includes('dragon')) return 'tag-green';
    if (lowerTag.includes('roc')) return 'tag-blue';
    if (lowerTag.includes('tulpar')) return 'tag-black';
    if (lowerTag.includes('garuda')) return 'tag-yellow';
    if (lowerTag.includes('unassigned')) return 'tag-gray';

    return 'tag-gray';
};

const getYearTagClass = (tag) => {
    if (tag === 4) return 'tag-purple';
    if (tag === 3) return 'tag-teal';
    if (tag === 2) return 'tag-pink';
    if (tag === 1) return 'tag-light-yellow';

    return 'tag-gray';
}

const MemberCard = ({ member, onChangeSuccess, canEdit }) => {
    const user_id = member.id;
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        full_name: member.name,
        house: member.house,
        year: member.year,
    });
    const [visibleCommittees, setVisibleCommittees] = useState(member.committees || []);
    const [allCommittees, setAllCommittees] = useState([]);
    const [toBeAddedCommittees, setToBeAddedCommittees] = useState([]);
    const [toBeDeletedCommittees, setToBeDeletedCommittees] = useState([]);
    const [isOptionsVisible, setIsOptionsVisible] = useState(false);
    
    useEffect(() => {
        setVisibleCommittees(member.committees || []);
    }, [member.committees]);

    // Function to toggle the options box visibility
    const handleOptionsClick = () => {
        setIsOptionsVisible(!isOptionsVisible);
    };

    // Placeholder functions
    const handleEdit = async () => {
        const data = await getCommittees(); 
        setAllCommittees(data);
        setIsEditing(true);
        setIsOptionsVisible(false);
    };

    const handleAddCommitteeChange = async (e) => {
        const committeeName = e.target.value;
        if (!committeeName) return;

        try {
            const committeeId = await getIdFromCommittee(committeeName);
            
            setToBeAddedCommittees(prev => [...prev, committeeId]);
            setVisibleCommittees(prev => [...prev, committeeName]);
            
            // Reset dropdown selection
            e.target.value = "";
        } catch (err) {
            alert("Error adding committee: " + err.message);
        }
    };

    const handlePendingDeleteCommClick = async (committeeName) => {
        try {
            const committeeId = await getIdFromCommittee(committeeName);
            
            setToBeDeletedCommittees((prev) => [...prev, committeeId]);
            setVisibleCommittees((prev) => prev.filter(name => name !== committeeName));

        } catch (err) {
            console.error("Could not find the ID for this committee:", err);
            alert("Error identifying committee for deletion.");
        }
    };

    const handleSubmit = async (e) => {
        //console.log(member);
        e.preventDefault();
        try {
            await updateStudent(user_id, editData);

            if (toBeAddedCommittees.length > 0) {
                await Promise.all(
                    toBeAddedCommittees.map(id =>
                        addCommitteeMembership(member.id, id))
                );
            }

            if (toBeDeletedCommittees.length > 0) {
                await Promise.all(
                    toBeDeletedCommittees.map(committeeId => 
                        deleteCommitteeMembership(member.id, committeeId)
                    )
                );
            }

            setIsEditing(false);
            setToBeDeletedCommittees([]);
            setToBeAddedCommittees([]);
            onChangeSuccess();
        } catch (error) {
            alert("Error updating member: " + error);
        }
    };

    const handleCancel = async () => {
        setToBeDeletedCommittees([]);
        setVisibleCommittees(member.committees || []);
        setIsEditing(false);
    }

    const handleDelete = async () => {
        const isConfirmed = window.confirm(`Are you sure you want to delete ${member.name}? This action cannot be undone.`);
        
        if (isConfirmed) {
            try {
                await deleteStudent(user_id);
                onChangeSuccess();
                alert('Member deleted successfully.');
            } catch (error) {
                console.log(error);
                alert('Delete unsuccessful: ' + error.message);
            }
        }
        setIsOptionsVisible(false); // Close menu after action
    };

    return (
    <div className="member-card">
        <div className="card-header">
            <div className="avatar-wrapper">
                <div className="avatar">{member.initials}</div>
                {member.status === 'active' && <div className="status-dot"></div>}
            </div>
            <div className="member-info">
                {isEditing ? (
                    <input
                        className='member-name-input'
                        type="text"
                        value={editData.full_name}
                        onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                    /> 
                ) : (
                    <h4>{member.name}</h4>
                )}
                
                <p>{member.handle}</p>
            </div>
            {/* Add the onClick event handler */}
            {canEdit && (
                <div className="more-options" onClick={handleOptionsClick}>
                    <BsThreeDots />
                    {isOptionsVisible && (
                        <div className="options-box" onClick={(e) => e.stopPropagation()}>
                            <ul>
                                <li>
                                    <button onClick={handleEdit}>Edit Member</button>
                                </li>
                                <li>
                                    <button onClick={handleDelete}>Delete Member</button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>

        <div className="card-body">
            <div className="tags">
                {isEditing ? (
                    <>
                        {/* Year Input */}
                        <select 
                        className={`tag ${getYearTagClass(member.year)}`}
                        value={editData.year}
                        onChange={(e) => setEditData({...editData, year: e.target.value})}
                        >
                            {[1,2,3,4].map(h => (
                                <option key={h} value={h}>{"Year " + h}</option>
                            ))}
                        </select>

                        {/* House Dropdown */}
                        <select 
                        className={`tag ${getTagClass(member.house)}`}
                        value={editData.house}
                        onChange={(e) => setEditData({...editData, house: e.target.value})}
                        >
                            {['Roc', 'Dragon', 'Garuda', 'Phoenix', 'Tulpar'].map(h => (
                                <option key={h} value={h}>{h}</option>
                            ))}
                        </select>

                        {/* Committee tags */}
                        {visibleCommittees.map(committee => (
                            <span key={committee} className="tag tag-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                {committee}
                                {isEditing && (
                                    <button 
                                        type="button"
                                        className="tag-delete-btn"
                                        onClick={() => handlePendingDeleteCommClick(committee)}
                                    >
                                        x
                                    </button>
                                )}
                            </span>
                        ))}

                        {/* Add Committee button */}                            
                        <select 
                            className="tag tag-gray add-dropdown" 
                            onChange={handleAddCommitteeChange}
                            defaultValue=""
                        >
                            <option value="" disabled>+ Add Committee</option>
                            {allCommittees
                                .filter(c => !visibleCommittees.includes(c.name)) // Don't show already assigned ones
                                .map(c => (
                                    <option key={c.committee_id} value={c.name}>
                                        {c.name}
                                    </option>
                                ))
                            }
                        </select>
                    </>
                    ) : (
                        <>
                        {/* Year and House tags */}
                        {member.year && <span className={`tag ${getYearTagClass(member.year)}`}>{"Year " + member.year}</span>}
                        {member.house && <span className={`tag ${getTagClass(member.house)}`}>{member.house}</span>}

                        {/* Role tag */}
                        {member.role && <span className="tag tag-blue">{member.role}</span>}
                        
                        {/* Committee tags */}
                        {visibleCommittees.map(committee => (
                            <span key={committee} className="tag tag-gray">
                                {committee}
                                {isEditing && (
                                    <button 
                                        type="button"
                                        className="tag-delete-btn"
                                        onClick={() => alert("delete")}
                                    >
                                        ×
                                    </button>
                                )}
                            </span>
                        ))}
                        </>  
                    )
                }
            </div>
        </div>

        <div className="card-actions">
            {isEditing ? (
                <>
                <button onClick={handleSubmit} className="btn-save">Save</button>
                <button onClick={handleCancel} className="btn-cancel">Cancel</button>
                </>
            ) : (
                <></>
            )}
        </div>
    </div>
  );
};

export default MemberCard;